import { ApolloError, useQuery } from '@apollo/client'
import { Option } from '@joor/design-system'

import { getBrandConnectedAccounts } from 'utils/transformations/accounts'
import { relayConnectionToArray } from 'utils/transformations/graphql'
import { naturalSortBy } from 'utils/transformations/sorting'

import {
  formatConnectedAccountsOptions,
  formatDropdownOptions,
} from '../../ImportOrders/SecondStep/SecondStep.hooks'
import {
  CONNECTED_ACCOUNTS_QUERY,
  INITIAL_CREATE_ORDER_TEMPLATE_QUERY,
  RETAILER_PRICETYPES_AND_DOORS,
} from './StandardOrderTemplate.queries'
import {
  InitialCreateOrderTemplateQueryData,
  PriceType,
} from './StandardOrderTemplate.types'

export const useConnectedAccounts = (retailerSearchText: string) => {
  const { data, error, loading } = useQuery(CONNECTED_ACCOUNTS_QUERY, {
    variables: {
      searchFilter: retailerSearchText,
    },
    skip: retailerSearchText.length < 3,
  })

  return {
    connectedAccountsQueryLoading: loading,
    connectedAccountsOptions: (!loading && !error && data?.connectedAccounts
      ? formatConnectedAccountsOptions(
          getBrandConnectedAccounts(data?.connectedAccounts),
        )
      : []) as Option[],
  }
}

const formatPriceTypeOptions = (priceTypes: PriceType[]) =>
  naturalSortBy(
    priceTypes.map((priceType) => ({
      id: priceType.id,
      value: priceType.id,
      label:
        priceType.name === ''
          ? priceType.currency.code
          : `${priceType.currency.code} - ${priceType.name}`,
    })),
    'label',
  )

export const initialCreateOrderTemplateData = ({
  data,
  loading,
  error,
}: {
  data: InitialCreateOrderTemplateQueryData
  loading: boolean
  error: ApolloError | undefined
}) => ({
  initialCreateOrderTemplateLoading: loading,
  initialCreateOrderTemplateError: error,
  collectionOptions:
    !loading && !error && data?.collections
      ? formatDropdownOptions(relayConnectionToArray(data?.collections))
      : [],
  orderEventsOptions:
    !loading && !error && data?.viewerBrand
      ? formatDropdownOptions(
          relayConnectionToArray(data?.viewerBrand?.orderEvents),
        )
      : [],
  orderTypesOptions:
    !loading && !error && data?.viewerBrand
      ? formatDropdownOptions(relayConnectionToArray(data?.viewerBrand?.types))
      : [],
  brandPriceTypeOptions:
    !loading && !error && data?.viewerBrand
      ? formatPriceTypeOptions(
          relayConnectionToArray(data?.viewerBrand?.priceTypes),
        )
      : [],
})

export const useInitialCreateOrderTemplateInfo = () => {
  const { data, loading, error } = useQuery(INITIAL_CREATE_ORDER_TEMPLATE_QUERY)
  return initialCreateOrderTemplateData({ data, loading, error })
}

// @ts-expect-error
export const retailerPriceTypesAndDoorsData = ({ data, loading, error }) => ({
  retailerPriceTypesAndDoorsLoading: loading,
  retailerPriceTypesAndDoorsError: error,
  retailerDoorOptions: data
    ? formatDropdownOptions(
        relayConnectionToArray(data?.node?.brandDoorMatches),
      )
    : [],
  retailerPriceTypeOptions: data
    ? formatPriceTypeOptions(
        relayConnectionToArray(data?.node?.priceTypeMatches),
      )
    : [],
})

export const useRetailerPriceTypesAndDoors = (selectedRetailerId?: string) => {
  const { data, loading, error } = useQuery(RETAILER_PRICETYPES_AND_DOORS, {
    variables: { retailerAccountId: selectedRetailerId },
    skip: !selectedRetailerId,
  })
  return retailerPriceTypesAndDoorsData({ data, loading, error })
}
